import type { ZodType } from "zod";
import { TRPCClientError } from "@trpc/client";
import { isRevenueCatNetworkError } from "@utils/revenuecatErrors";
import type { QueryClient, QueryKey, Updater } from "@tanstack/react-query";
import { eq, inArray, getTableColumns } from "drizzle-orm";
import { queryCache } from "@sqlite/schema";
import type { MobileDatabase } from "../../runtime/ports";
import { createEntityCache } from "./createEntityCache";
import { createInfiniteQueryCache } from "./createInfiniteQueryCache";
import { describeError } from "./describeError";
import { createQueryCacheWrite } from "./queryCacheWrite";
import { stableStringify } from "./stableStringify";

/**
 * Thrown by a queryFn's `validate` callback when the server response violates
 * the query's invariant (for example: an empty library for a logged-in user
 * — see hooks/trpc/useLibrary). It propagates without overwriting the
 * previous good value.
 */
export class EmptyServerResponseError extends Error {
  public readonly queryKey: readonly unknown[];
  constructor(queryKey: readonly unknown[]) {
    super(`Empty server response for ${stableStringify(queryKey)}`);
    this.name = "EmptyServerResponseError";
    this.queryKey = queryKey;
  }
}

/** A failure to reach a server, from any client the app reads through:
 *  tRPC without server data, or with SERVICE_UNAVAILABLE or TIMEOUT, and
 *  RevenueCat's network and offline errors. Only these serve a cached row. */
export function isNetworkClass(err: unknown): boolean {
  return (
    (err instanceof TRPCClientError &&
      (!err.data ||
        err.data.code === "SERVICE_UNAVAILABLE" ||
        err.data.code === "TIMEOUT")) ||
    isRevenueCatNetworkError(err)
  );
}

interface WithDurableCacheOpts<T> {
  queryKey: readonly unknown[];
  fetch: () => Promise<T>;
  /**
   * Optional invariant. Throw `EmptyServerResponseError` (or any Error) to
   * reject the response. Use for queries with a "this value should never be
   * empty" contract — the validate runs *after* a
   * successful fetch, so the cache row is not overwritten with bad data.
   */
  validate?: (data: T) => void;
  /** Callers may further restrict the shared network-only fallback policy. */
  fallbackOnError?: (error: unknown) => boolean;
  /**
   * Required zod schema for the response shape:
   *   • Fresh responses are parsed before being written to the cache.
   *     A parse error propagates without overwriting the cached row.
   *   • Cached rows are parsed before being returned. A parse failure
   *     means the cache row is from a previous app version with an
   *     incompatible shape — the row is deleted and treated as a miss.
   *
   * This is how the cache survives destructive API shape changes: rows
   * written by an old build that fail the new schema get purged on first
   * read by the new build, then refilled by the next fresh fetch. It is
   * also why an app update no longer wipes the cache (see the buster in
   * `data/core/queryClient.ts`), so no saved query may skip it.
   */
  schema: ZodType<T>;
}

/**
 * Wrap a queryFn with a SQLite-backed durable cache. The shape:
 *
 *   fetch + validate (own try)
 *     → write the row in a SEPARATE best-effort try (write failure logged
 *       but never swallows the fresh data)
 *     → return fresh data
 *   if the network failed
 *     → read the cached row; if present return it (queryFn appears to
 *       succeed to react-query); otherwise rethrow. Other errors propagate.
 *
 * Most callers should use `durableQueryOptions` instead of wiring
 * `withDurableCache` + `initialData` + `initialDataUpdatedAt` by hand —
 * the helper composes them into a UseQueryOptions you spread into your
 * hook, with the row's real `fetchedAt` driving `initialDataUpdatedAt`
 * so per-query staleTime is respected on cold-start.
 */
export function createDurableCache({
  db,
  isCurrent = () => true,
  canWrite = () => isCurrent(),
  sqlite = { queryCache, eq, inArray, getTableColumns },
}: {
  db: MobileDatabase;
  isCurrent?: () => boolean;
  canWrite?: (queryKey: readonly unknown[]) => boolean;
  sqlite?: {
    queryCache: typeof queryCache;
    eq: typeof eq;
    inArray: typeof inArray;
    getTableColumns: typeof getTableColumns;
  };
}) {
  const { queryCache, eq } = sqlite;
  const queryCacheWrite = createQueryCacheWrite(db, sqlite);
  // A response fetched before a resource was deleted must not restore its row.
  const deletionVersions = new Map<string, number>();

  async function withDurableCache<T>(
    opts: WithDurableCacheOpts<T>,
  ): Promise<T> {
    const key = stableStringify(opts.queryKey);
    const deletionVersion = deletionVersions.get(key);
    const canPersist = () =>
      canWrite(opts.queryKey) && deletionVersions.get(key) === deletionVersion;
    let validated: T;
    try {
      const fresh = await opts.fetch();
      // Reject bad server data without overwriting the last valid row.
      validated = opts.schema.parse(fresh);
      if (opts.validate) opts.validate(validated);
    } catch (err) {
      if (!isNetworkClass(err)) throw err;
      if (opts.fallbackOnError && !opts.fallbackOnError(err)) throw err;
      const cached = readQueryCacheSync<T>(opts.queryKey, opts.schema);
      if (cached !== undefined) return cached;
      throw err;
    }

    // Best-effort persist. Done in its own try so a SQLite hiccup (migration
    // race, disk error, web mock without onConflictDoUpdate) never causes a
    // perfectly good network response to be discarded in favor of a stale
    // cached row. UI gets the fresh data either way.
    if (!canPersist()) return validated;
    try {
      const write = queryCacheWrite(opts.queryKey, validated);
      await Promise.resolve().then(() => {
        if (write && canPersist()) write.run();
      });
    } catch (writeErr) {
      console.warn(
        "[durableCache] write-through failed (returning fresh data)",
        {
          queryKey: stableStringify(opts.queryKey),
          error: describeError(writeErr),
        },
      );
    }

    return validated;
  }

  /**
   * Optimistic write-through. Updates the in-memory react-query cache AND
   * best-effort writes the new value to the durable SQLite cache. Use this
   * at any optimistic-mutation site whose update should survive a kill-and-
   * relaunch or a bad-internet refetch.
   *
   * Without this, an optimistic `setQueryData` can be lost in two ways:
   *   • App is force-quit between optimistic update and server confirmation
   *     → MMKV may or may not have caught the in-memory update; on relaunch
   *     the user sees pre-mutation state.
   *   • Server mutation succeeds but the follow-up refetch fails on bad
   *     internet → `withDurableCache` returns the (pre-mutation) SQLite row,
   *     visibly reverting the like/unlike etc.
   *
   * Both go away if the optimistic update writes through to SQLite at the
   * time it's authored — the durable cache stays in sync with what the user
   * sees, and `withDurableCache.catch` returns the post-mutation state.
   *
   * A null value stays in memory and deletes the durable row; undefined
   * leaves persistence unchanged.
   *
   * The SQLite write is fire-and-forget: UI feedback is never blocked on
   * disk, and a write failure is logged but never thrown.
   */
  function setQueryDataDurable<T>(
    queryClient: QueryClient,
    queryKey: QueryKey,
    updater: Updater<T | undefined, T | undefined>,
  ): T | undefined {
    if (!canWrite(queryKey)) return undefined;
    const next = queryClient.setQueryData<T>(queryKey, updater);
    if (next === undefined) return next;

    // Fire-and-forget. Caller does not await disk; failures are logged.
    const write = queryCacheWrite(queryKey, next);

    Promise.resolve()
      .then(() => {
        if (write && canWrite(queryKey)) write.run();
      })
      .catch((err) =>
        console.warn("[durableCache] setQueryDataDurable write failed", {
          queryKey: stableStringify(queryKey),
          error: describeError(err),
        }),
      );

    return next;
  }

  /**
   * Delete a query's durable row and leave the in-memory query alone. For a
   * resource that no longer exists, such as a deleted playlist: the screen
   * still showing it keeps its data until it unmounts, and a relaunch or an
   * offline read no longer finds it. Existing `withDurableCache` fetches cannot
   * restore the row. Fire-and-forget, like `setQueryDataDurable`.
   */
  function deleteDurableRow(queryKey: QueryKey): void {
    if (!canWrite(queryKey)) return;
    const key = stableStringify(queryKey);
    deletionVersions.set(key, (deletionVersions.get(key) ?? 0) + 1);
    const write = queryCacheWrite(queryKey, null);

    Promise.resolve()
      .then(() => {
        if (write && canWrite(queryKey)) write.run();
      })
      .catch((err) =>
        console.warn("[durableCache] deleteDurableRow failed", {
          queryKey: stableStringify(queryKey),
          error: describeError(err),
        }),
      );
  }

  /**
   * Synchronous read of a cached query row. Returns `undefined` if no row, or
   * if the cached row fails `schema` (in which case the row is also deleted
   * so it can't poison subsequent reads).
   *
   * Safe to call from `useQuery({ initialData })` — `expo-sqlite`'s
   * `openDatabaseSync` plus drizzle's `.all()` are sync, and a single-row PK
   * lookup is well under a frame for our workload.
   */
  function readQueryCacheSync<T>(
    queryKey: readonly unknown[],
    schema: ZodType<T>,
  ): T | undefined {
    return readQueryCacheRowSync<T>(queryKey, schema)?.data;
  }

  /**
   * Like `readQueryCacheSync` but also exposes the row's `fetchedAt`
   * timestamp. Used by `durableQuery` to set `initialDataUpdatedAt`, so
   * react-query respects per-query `staleTime` on cold-start (rather than
   * treating every initialData hit as immediately stale and refetching).
   */
  function readQueryCacheRowSync<T>(
    queryKey: readonly unknown[],
    schema: ZodType<T>,
  ): { data: T; fetchedAt: Date } | undefined {
    if (!isCurrent()) return undefined;
    const key = stableStringify(queryKey);
    try {
      const rows = db
        .select()
        .from(queryCache)
        .where(eq(queryCache.queryKey, key))
        .all();
      if (!rows[0]) return undefined;

      const parsed = schema.safeParse(rows[0].data);
      if (parsed.success)
        return { data: parsed.data, fetchedAt: rows[0].fetchedAt };

      // Cached shape no longer matches the schema (e.g. app upgraded and the
      // API response shape changed). Drop the row so subsequent reads don't
      // keep returning stale data; the next successful fetch will repopulate.
      console.warn(
        "[durableCache] cached row failed schema validation, purging",
        { queryKey: key, error: parsed.error.message },
      );
      db.delete(queryCache).where(eq(queryCache.queryKey, key)).run();
      return undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Transform a standard `useQuery` options object into a durable-cached one.
   * Conforms to react-query's `UseQueryOptions` shape — accepts whatever
   * you'd normally pass to `useQuery` plus a `schema` (and optional
   * `validate`), returns a `UseQueryOptions` you spread into your hook.
   *
   * The caller's `queryFn` becomes the network fetch; this helper:
   *   • Wraps it with `withDurableCache` (success → write to SQLite; network
   *     error → fall back to cached row).
   *   • Adds `initialData` reading from SQLite (instant cold-start render).
   *   • Adds `initialDataUpdatedAt` from the row's real `fetchedAt`, so
   *     react-query honors `staleTime` on cold start instead of treating
   *     every initial render as immediately-stale.
   *   • Sets `networkMode: "always"` (cache + offline-first behavior).
   *
   * Auth gating is the caller's responsibility — put any `if
   * (!auth.currentUser) throw NoAuthError(...)` check at the top of your
   * `queryFn`. The helper stays framework-agnostic.
   *
   * Usage:
   *   export const gamificationSummaryQuery = () =>
   *     durableQueryOptions<GamificationSummaryQueryData>({
   *       queryKey: gamificationSummaryQueryKey(),
   *       queryFn: () => {
   *         if (!auth.currentUser) throw new NoAuthError("summaryQuery");
   *         return trpcVanillaClient.user.gamification.summary.query();
   *       },
   *       schema: gamificationSummaryWireSchema,
   *       staleTime: 30_000,  // standard react-query option
   *     });
   *
   *   const query = useQuery(gamificationSummaryQuery());
   */
  function durableQueryOptions<T, TQueryKey extends QueryKey = QueryKey>(opts: {
    queryKey: TQueryKey;
    queryFn: () => Promise<T>;
    schema: ZodType<T>;
    /** Optional invariant. See WithDurableCacheOpts.validate. */
    validate?: (data: T) => void;
    fallbackOnError?: (error: unknown) => boolean;
    staleTime?: number;
    gcTime?: number;
    refetchOnMount?: boolean | "always";
    refetchOnWindowFocus?: boolean | "always";
    refetchOnReconnect?: boolean | "always";
    refetchInterval?: number | false;
    structuralSharing?: boolean;
    meta?: Record<string, unknown>;
    subscribed?: boolean;
  }) {
    const { schema, validate, fallbackOnError, queryFn, queryKey, ...rest } =
      opts;
    return {
      networkMode: "always" as const,
      ...rest,
      queryKey,
      queryFn: () =>
        withDurableCache<T>({
          queryKey,
          fetch: queryFn,
          schema,
          validate,
          fallbackOnError,
        }),
      initialData: () => readQueryCacheRowSync<T>(queryKey, schema)?.data,
      initialDataUpdatedAt: () =>
        readQueryCacheRowSync<T>(queryKey, schema)?.fetchedAt?.getTime() ?? 0,
    };
  }

  return {
    withDurableCache,
    setQueryDataDurable,
    deleteDurableRow,
    readQueryCacheSync,
    readQueryCacheRowSync,
    durableQueryOptions,
    ...createEntityCache({ db, isCurrent, canWrite, sqlite }),
    ...createInfiniteQueryCache({ db, isCurrent, canWrite, sqlite }),
  };
}

export type DurableCache = ReturnType<typeof createDurableCache>;
